import { mapTint } from './presentation'
import { mapMotionOptions, motionKey, motionPhase } from './motion-options'

// 单张透明Canvas只绘制光晕，不参与命中，也不逐帧setOption或请求数据。
export function createMapMotion (canvas, environment = {}) {
  const browser = environment.window || window, doc = environment.document || document
  const ctx = canvas.getContext('2d'), now = environment.now || (() => browser.performance.now())
  const request = environment.request || browser.requestAnimationFrame.bind(browser)
  const cancel = environment.cancel || browser.cancelAnimationFrame.bind(browser)
  const media = browser.matchMedia && browser.matchMedia('(prefers-reduced-motion: reduce)')
  let disposed = false, offscreen = false, frame = null, lastPaint = -Infinity, width = 0, height = 0, ratio = 1
  let settings = mapMotionOptions(), option = {}, rows = [], points = [], project, bounds, hovered = '', hoveredRow
  let timestamps = new Map(), flashes = new Map()
  const paused = () => disposed || offscreen || doc.hidden || !!(media && media.matches)
  const enabled = () => settings.pointEffect !== 'normal' && settings.motionStrength > 0
  const clear = () => { if (ctx) ctx.clearRect(0, 0, width, height) }
  const stop = () => { if (frame !== null) cancel(frame); frame = null; clear() }
  const budget = () => Math.max(16, Math.min(120, Math.floor(width * height / 6000)))
  const active = time => points.some(point => settings.pointEffect !== 'ripple' || point.key === hovered || point.until > time)

  function reproject () {
    points = []
    if (!ctx || !enabled() || !project || !width || !height) return
    // 原始位置先按经纬范围剔除，屏幕投影最多处理可见动效预算，不随设备总数逐帧增长。
    const candidates = settings.pointEffect === 'ripple' && hoveredRow ? [hoveredRow, ...rows.filter(row => motionKey(row) !== hovered)] : rows
    for (const row of candidates) {
      if (!Number.isFinite(row.lng) || !Number.isFinite(row.lat)) continue
      if (bounds && (row.lng < bounds[0] || row.lat < bounds[1] || row.lng > bounds[2] || row.lat > bounds[3])) continue
      const pixel = project([row.lng, row.lat])
      if (!Array.isArray(pixel) || !pixel.every(Number.isFinite) || pixel[0] < -28 || pixel[1] < -28 || pixel[0] > width + 28 || pixel[1] > height + 28) continue
      const key = motionKey(row), region = !!row.regionCount, cluster = !region && !!row.clusterRows
      // 区域只在已有代表锚点播放光效，不把数量变化当作设备位置上报。
      let until = region ? 0 : cluster ? Number(row.motionUntil) || 0 : flashes.get(row.deviceId) || 0
      if (cluster && !until) (row.clusterRows || []).forEach(item => { until = Math.max(until, flashes.get(item.deviceId) || 0) })
      const scale = Math.max(.7, Math.min(1.6, Number(option.clusterSize) || 1))
      points.push({ key, lng: row.lng, lat: row.lat, x: pixel[0], y: pixel[1], phase: motionPhase(key), cluster, region, until,
        radius: region ? 3.5 * scale : cluster ? 9 * scale : Math.max(2, Math.min(20, (Number(option.pointSize) || 10) / 2)),
        color: mapTint(settings.motionColor || (region || cluster ? option.clusterColor : option.pointColor) || '#40dcff') })
      if (points.length >= budget()) break
    }
  }

  function draw (time) {
    clear()
    const strength = settings.motionStrength / 100, period = settings.motionPeriod * 1000
    points.forEach(point => {
      const hover = point.key === hovered
      if (settings.pointEffect === 'ripple' && !hover && point.until <= time) return
      const phase = (time / period + point.phase) % 1, pulse = (1 - Math.cos(phase * Math.PI * 2)) / 2
      const radius = point.radius, glow = radius * (1.7 + pulse * .55) + 4
      const flash = settings.locationUpdateFlash ? Math.max(0, Math.min(1, (point.until - time) / 900)) : 0
      ctx.save(); ctx.translate(point.x, point.y)
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, glow)
      gradient.addColorStop(0, point.color); gradient.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.globalAlpha = strength * (.12 + pulse * .12 + flash * .3); ctx.fillStyle = gradient
      ctx.beginPath(); ctx.arc(0, 0, glow, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = point.color; ctx.lineWidth = 1
      if (settings.pointEffect === 'breath') {
        ctx.globalAlpha = strength * (.18 + pulse * .32)
        ctx.beginPath(); ctx.arc(0, 0, radius * (1.4 + pulse * .35), 0, Math.PI * 2); ctx.stroke()
      } else {
        for (let index = 0; index < 2; index++) {
          const wave = (phase + index / 2) % 1
          ctx.globalAlpha = strength * Math.pow(1 - wave, 2) * (point.cluster ? .4 : .65)
          ctx.beginPath(); ctx.arc(0, 0, radius * (1.15 + wave * 2.2) + wave * 3, 0, Math.PI * 2); ctx.stroke()
        }
      }
      if (flash > 0) {
        ctx.globalAlpha = strength * flash * .7; ctx.strokeStyle = '#d9faff'; ctx.lineWidth = 1.5
        ctx.beginPath(); ctx.arc(0, 0, radius * (1.15 + (1 - flash) * 1.2), 0, Math.PI * 2); ctx.stroke()
      }
      ctx.restore()
    })
  }

  function tick (time) {
    frame = null
    if (paused() || !enabled() || !active(time)) { clear(); return }
    if (time - lastPaint >= 1000 / 30) { draw(time); lastPaint = time }
    frame = request(tick)
  }

  function refresh () {
    if (paused() || !enabled() || !points.length || !active(now())) { stop(); return }
    draw(now())
    if (frame === null) frame = request(tick)
  }

  function visibility () {
    flashes.clear(); points.forEach(point => { point.until = 0 })
    if (paused()) stop()
    else refresh()
  }
  doc.addEventListener('visibilitychange', visibility)
  if (media && media.addEventListener) media.addEventListener('change', visibility)
  else if (media && media.addListener) media.addListener(visibility)
  const observer = browser.IntersectionObserver ? new browser.IntersectionObserver(entries => {
    const entry = entries.find(item => item.target === canvas)
    if (!disposed && entry) { offscreen = !entry.isIntersecting; visibility() }
  }) : null
  if (observer) observer.observe(canvas)

  return {
    observe (values = [], change = {}) {
      if (disposed) return
      const ids = new Set(change.source === 'realtime' ? change.positionUpdates || [] : [])
      if (change.source !== 'realtime') flashes.clear()
      const next = new Map(), time = now()
      values.forEach(row => {
        next.set(row.deviceId, row.timestamp)
        if (!paused() && settings.locationUpdateFlash && ids.has(row.deviceId) &&
            Number.isFinite(row.timestamp) && (!timestamps.has(row.deviceId) || row.timestamp > timestamps.get(row.deviceId))) flashes.set(row.deviceId, time + 900)
      })
      timestamps = next
      flashes.forEach((until, id) => { if (!next.has(id) || until <= time) flashes.delete(id) })
    },
    highlightUntil (id) { return settings.locationUpdateFlash ? flashes.get(id) || 0 : 0 },
    update (values, input, geometry) {
      if (disposed) return
      settings = mapMotionOptions(input); option = input; project = geometry.project; bounds = geometry.bounds
      rows = enabled() ? values.map(row => ({ row, priority: motionPhase(motionKey(row)) })).sort((a, b) => a.priority - b.priority).map(item => item.row) : []
      hoveredRow = hovered ? rows.find(row => motionKey(row) === hovered) || null : null
      if (!hoveredRow) hovered = ''
      const nextWidth = Math.max(0, geometry.width), nextHeight = Math.max(0, geometry.height)
      const nextRatio = Math.max(1, Math.min(2, browser.devicePixelRatio || 1))
      if (nextWidth !== width || nextHeight !== height || nextRatio !== ratio) {
        width = nextWidth; height = nextHeight; ratio = nextRatio
        canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio)
        if (ctx) ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      }
      if (!settings.locationUpdateFlash) flashes.clear()
      reproject(); refresh()
    },
    view (geometry) { if (!disposed) { project = geometry.project; bounds = geometry.bounds; reproject(); refresh() } },
    hover (row) {
      if (disposed) return
      hoveredRow = row ? rows.find(item => motionKey(item) === motionKey(row)) || null : null
      hovered = hoveredRow ? motionKey(hoveredRow) : ''
      if (settings.pointEffect === 'ripple') reproject()
      refresh()
    },
    inspect () { return { count: points.length, budget: budget(), running: frame !== null, points: points.map(point => ({ ...point })) } },
    dispose () {
      if (disposed) return
      disposed = true; stop(); rows = []; points = []; hoveredRow = null; timestamps.clear(); flashes.clear()
      doc.removeEventListener('visibilitychange', visibility)
      if (observer) observer.disconnect()
      if (media && media.removeEventListener) media.removeEventListener('change', visibility)
      else if (media && media.removeListener) media.removeListener(visibility)
    }
  }
}
